<script setup lang="ts">
const { t } = useI18n()

interface Props {
  modelValue: string | null
  placeholder?: string
  disabled?: boolean
  showList?: boolean
  title?: string
  helpText?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  disabled: false,
  showList: true,
  title: undefined,
  helpText: undefined
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const inputValue = ref('')
const skuError = ref('')

const skus = computed(() => {
  if (!props.modelValue) return []
  return props.modelValue
    .split(',')
    .map((sku) => sku.trim())
    .filter((sku) => sku.length > 0)
    .filter((sku, index, array) => array.indexOf(sku) === index)
})

const updateModelValue = (newSkus: string[]) => {
  const value = newSkus.join(',')
  emit('update:modelValue', value)
}

const addSku = (sku: string) => {
  const trimmedSku = sku.trim()
  skuError.value = ''

  if (!trimmedSku || trimmedSku.length === 0) {
    return
  }

  if (trimmedSku.length > 50) {
    skuError.value = t('components.productAssociation.skuTooLong')
    return
  }

  if (skus.value.includes(trimmedSku)) {
    inputValue.value = ''
    return
  }

  const newSkus = [...skus.value, trimmedSku]
  updateModelValue(newSkus)
  inputValue.value = ''
}

const removeSku = (skuToRemove: string) => {
  const newSkus = skus.value.filter((sku) => sku !== skuToRemove)
  updateModelValue(newSkus)
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) {
      inputValue.value = ''
    }
  },
  { immediate: true }
)
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center gap-2">
      <h4 class="text-sm font-medium leading-6 text-foreground flex-1">
        {{ title ?? t('pages.reward.productAssociation.manualSkuTitle') }}
      </h4>
    </div>

    <div v-if="showList" class="flex flex-wrap gap-1.5">
      <BaseChip v-for="sku in skus" :key="sku" :text="sku" :removable="!disabled" @remove="removeSku(sku)" />
    </div>

    <div class="flex flex-row justify-center gap-1.5">
      <AppFormInput
        id="externalSku"
        ref="inputRef"
        v-model="inputValue"
        type="text"
        class="flex-1"
        :placeholder="placeholder"
        :disabled="disabled"
        @keydown.enter="addSku(inputValue)"
      />

      <Button
        :aria-label="t('global.words.add')"
        :disabled="inputValue.length === 0 || disabled"
        variant="default-primary"
        class="!px-3 !py-2 h-10"
        @click="addSku(inputValue)"
      >
        <BaseIcon name="HeroPlusIconSolid" class="h-4 w-4" />
      </Button>
    </div>

    <p v-if="skuError" class="text-xs font-medium text-red-500">{{ skuError }}</p>
    <p v-else-if="helpText" class="text-[11px] text-muted-foreground">
      {{ helpText }}
    </p>
  </div>
</template>
